import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Button,
  Container,
  Divider,
  Flex,
  Heading,
  HStack,
  Icon,
  IconButton,
  Image,
  SimpleGrid,
  Skeleton,
  Spacer,
  Text,
  Tooltip,
  useToast,
  VStack,
} from '@chakra-ui/react';
import React, { useCallback } from 'react';
import {
  FiAlertCircle,
  FiArrowLeft,
  FiCopy,
  FiExternalLink,
  FiPrinter,
  FiRefreshCw,
} from 'react-icons/fi';
import { Link as RouterLink } from 'react-router-dom';
import { LinkDeTerminal, useTerminalLinks } from '../../hooks/useTerminalLinks';

/**
 * Links dos terminais — a tela que o treinador abre para montar os tablets.
 *
 * Substitui os cinco botoes de Modules.tsx, que abriam URLs escritas a mao sem evento e sem
 * sala. Aqui cada porta ja vem com a sala certa no link, entao o operador nao escolhe sala
 * nenhuma: abre o link uma vez e o tablet fica configurado.
 *
 * Sem entidade de terminal, sem autenticacao de dispositivo — isso vem depois. O ganho que
 * esta tela entrega hoje e o que importa na montagem: ninguem digita URL e ninguem erra a sala.
 */

const CSS_IMPRESSAO = `
@media print {
  body * { visibility: hidden; }
  .folha-terminais, .folha-terminais * { visibility: visible; }
  .folha-terminais {
    position: absolute; left: 0; top: 0; width: 100%;
  }
  .nao-imprimir { display: none !important; }
  .cartao-terminal {
    break-inside: avoid; page-break-inside: avoid;
    border: 1px solid #999 !important; box-shadow: none !important;
  }
}
`;

const CartaoTerminal: React.FC<{ link: LinkDeTerminal; onCopiar: (l: LinkDeTerminal) => void }> = ({
  link,
  onCopiar,
}) => (
  <Box
    className="cartao-terminal"
    borderWidth="1px"
    borderColor="gray.200"
    borderRadius="lg"
    bg="white"
    p={5}
  >
    <VStack align="stretch" spacing={4}>
      <Box minH="52px">
        {/* Nomes de sala reais chegam a 45 caracteres — a caixa cresce, o texto nao trunca. */}
        <Heading size="sm" lineHeight="1.3" wordBreak="break-word">
          {link.titulo}
        </Heading>
        <Text fontSize="sm" color="gray.600" mt={1}>
          {link.subtitulo}
        </Text>
      </Box>

      <Flex justify="center" bg="gray.50" borderRadius="md" py={4}>
        {link.qrDataUrl ? (
          <Image
            src={link.qrDataUrl}
            alt={`QR code para abrir ${link.titulo}`}
            boxSize="160px"
            sx={{ imageRendering: 'pixelated' }}
          />
        ) : (
          <Skeleton boxSize="160px" borderRadius="md" />
        )}
      </Flex>

      <Box>
        <Text fontSize="xs" color="gray.500" textTransform="uppercase" fontWeight="semibold" mb={1}>
          Endereco
        </Text>
        <Text fontSize="xs" fontFamily="mono" color="gray.700" wordBreak="break-all">
          {link.url}
        </Text>
      </Box>

      <HStack className="nao-imprimir" spacing={2}>
        <Button
          leftIcon={<FiCopy />}
          size="md"
          height="40px"
          flex="1"
          onClick={() => onCopiar(link)}
        >
          Copiar link
        </Button>
        <Tooltip label="Abrir numa aba nova" hasArrow>
          <IconButton
            aria-label={`Abrir ${link.titulo} numa aba nova`}
            icon={<FiExternalLink />}
            size="md"
            height="40px"
            minW="40px"
            onClick={() => window.open(link.url, '_blank', 'noopener,noreferrer')}
          />
        </Tooltip>
      </HStack>
    </VStack>
  </Box>
);

const TerminalLinksPage: React.FC = () => {
  const { portas, fixos, loading, error, recarregar } = useTerminalLinks();
  const toast = useToast();

  const copiar = useCallback(
    async (link: LinkDeTerminal) => {
      try {
        await navigator.clipboard.writeText(link.url);
        toast({
          title: 'Link copiado',
          description: link.titulo,
          status: 'success',
          duration: 2500,
          isClosable: true,
        });
      } catch {
        // clipboard exige contexto seguro; em http o navegador recusa sem avisar o usuario.
        toast({
          title: 'Nao foi possivel copiar',
          description: 'Selecione o endereco no cartao e copie manualmente.',
          status: 'warning',
          duration: 5000,
          isClosable: true,
        });
      }
    },
    [toast],
  );

  return (
    <Container maxW="7xl" py={8}>
      <style>{CSS_IMPRESSAO}</style>

      <VStack align="stretch" spacing={6}>
        <Box className="nao-imprimir">
          <Breadcrumb fontSize="sm" color="gray.600" mb={3}>
            <BreadcrumbItem>
              <BreadcrumbLink as={RouterLink} to="/admin">
                <HStack spacing={1}>
                  <Icon as={FiArrowLeft} />
                  <Text>Painel</Text>
                </HStack>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbItem isCurrentPage>
              <BreadcrumbLink href="#">Links dos terminais</BreadcrumbLink>
            </BreadcrumbItem>
          </Breadcrumb>

          <Flex align="center" wrap="wrap" gap={3}>
            <Box>
              <Heading size="lg">Links dos terminais</Heading>
              <Text color="gray.600" mt={1}>
                Cada link abre o aplicativo ja configurado na sala certa. Abra uma vez no tablet e
                adicione a tela de inicio.
              </Text>
            </Box>
            <Spacer />
            <HStack spacing={2}>
              <Tooltip label="Buscar as salas de novo" hasArrow>
                <IconButton
                  aria-label="Recarregar salas"
                  icon={<FiRefreshCw />}
                  height="40px"
                  minW="40px"
                  isLoading={loading}
                  onClick={recarregar}
                />
              </Tooltip>
              <Button
                leftIcon={<FiPrinter />}
                colorScheme="blue"
                height="40px"
                onClick={() => window.print()}
                isDisabled={loading || (!portas.length && !fixos.length)}
              >
                Imprimir folha
              </Button>
            </HStack>
          </Flex>
        </Box>

        {error && (
          <Alert status="error" borderRadius="md" className="nao-imprimir">
            <AlertIcon />
            <Box flex="1">
              <Text fontWeight="semibold">Nao foi possivel carregar as salas</Text>
              <Text fontSize="sm">{error}</Text>
            </Box>
            <Button size="sm" height="40px" onClick={recarregar}>
              Tentar de novo
            </Button>
          </Alert>
        )}

        <Alert status="info" borderRadius="md" className="nao-imprimir">
          <AlertIcon as={FiAlertCircle} />
          <Text fontSize="sm">
            Estes links nao pedem senha por sala, mas o modulo ainda pede o login do operador na
            primeira abertura. Depois disso o tablet nao pergunta mais.
          </Text>
        </Alert>

        <Box className="folha-terminais">
          <Box mb={8}>
            <HStack mb={3}>
              <Heading size="md">Portas de sala</Heading>
              {!loading && (
                <Badge colorScheme="blue" fontSize="sm">
                  {portas.length}
                </Badge>
              )}
            </HStack>
            <Divider mb={4} />

            {loading ? (
              <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={5}>
                {[0, 1, 2].map((i) => (
                  <Skeleton key={i} height="340px" borderRadius="lg" />
                ))}
              </SimpleGrid>
            ) : portas.length ? (
              <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={5}>
                {portas.map((l) => (
                  <CartaoTerminal key={l.chave} link={l} onCopiar={copiar} />
                ))}
              </SimpleGrid>
            ) : (
              <Box
                borderWidth="1px"
                borderStyle="dashed"
                borderColor="gray.300"
                borderRadius="lg"
                p={8}
                textAlign="center"
              >
                <Text color="gray.600">
                  Nenhuma sala cadastrada neste evento. Cadastre as salas para gerar os links das
                  portas.
                </Text>
              </Box>
            )}
          </Box>

          <Box>
            <Heading size="md" mb={3}>
              Balcao e totem
            </Heading>
            <Divider mb={4} />
            <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={5}>
              {fixos.map((l) => (
                <CartaoTerminal key={l.chave} link={l} onCopiar={copiar} />
              ))}
            </SimpleGrid>
          </Box>
        </Box>
      </VStack>
    </Container>
  );
};

export default TerminalLinksPage;
